import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogProps,
  FormLabel,
  InputGroup,
  InputMessage,
  InputText,
  Stack,
  Text,
} from "@aksara-ui/react";
import { Field, FieldProps, Form, Formik, FormikValues } from "formik";
import moment from "moment";
import React, { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { updateCatalogStock } from "redux/actions";
import styled from "styled-components";
import * as yup from "yup";

const FormWrapper = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

interface Props {
  catalogId: string;
  catalogStockId: string;
  type: string;
}

const initialValues = {
  uniqueCodes: "",
  pins: "",
  expiryDates: "",
};

const CatalogStockUpdateModal: React.FC<Props & DialogProps> = ({
  isOpen,
  onClose,
  catalogId,
  catalogStockId,
  type,
}) => {
  const dispatch = useDispatch();
  const formikRef = useRef<FormikValues>() as any;

  const { catalogStocks, isFormLoading } = useSelector(
    (state: any) => state.catalogStockReducer
  );

  const handleCloseModal = () => {
    onClose && onClose();
  };

  const handleSubmit = (values: any) => {
    const { uniqueCodes, pins, expiryDates } = values;
    dispatch(
      updateCatalogStock(
        catalogStockId,
        catalogId,
        { uniqueCodes, pins, expiryDates },
        handleCloseModal
      ) as any
    );
  };

  useEffect(() => {
    if (isOpen) {
      if (!!catalogStockId) {
        const catalogStockDetail = catalogStocks.find(
          (catalogStock: any) => catalogStock.id === parseInt(catalogStockId)
        );

        if (formikRef.current) {
          formikRef.current.setFieldValue(
            "uniqueCodes",
            catalogStockDetail.unique_code
          );
          if (type === "Voucher") {
            formikRef.current.setFieldValue(
              "expiryDates",
              moment(catalogStockDetail.expiry_date).format("YYYY-MM-DD")
            );
          }
        }
      }
    }
  }, [isOpen, catalogStocks, catalogStockId]);

  const validationSchema = yup.object({
    uniqueCodes: yup.string().required("Unique Code is required field"),
    pins: yup.string(),
    expiryDates: yup.string().required("Expiry Date is required field"),
  });

  return (
    <Dialog
      disableOverlayClick
      isOpen={isOpen}
      onClose={() => handleCloseModal()}
      overflowY={"auto"}
    >
      <Formik
        innerRef={formikRef}
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {() => {
          return (
            <Form>
              <DialogHeader>
                <Text fontWeight={700}>Update {type} Stock</Text>
              </DialogHeader>
              <DialogContent>
                <FormWrapper>
                  <Field name="uniqueCodes">
                    {({ field, meta }: FieldProps<string>) => (
                      <Stack spacing="xs">
                        <FormLabel htmlFor="uniqueCodes">Unique Code</FormLabel>
                        <InputGroup inputSize={40}>
                          <InputText
                            type="text"
                            size={40}
                            errors={!!meta.error && meta.touched}
                            {...field}
                          />
                        </InputGroup>
                        {!!meta.error && meta.touched && (
                          <InputMessage variant="error">
                            {meta.error}
                          </InputMessage>
                        )}
                      </Stack>
                    )}
                  </Field>

                  {type === "Voucher" ? (
                    <Field name="pins">
                      {({ field, meta }: FieldProps<string>) => (
                        <Stack spacing="xs">
                          <FormLabel htmlFor="pins">Pin</FormLabel>
                          <InputGroup inputSize={40}>
                            <InputText
                              type="password"
                              size={40}
                              errors={!!meta.error && meta.touched}
                              {...field}
                            />
                          </InputGroup>
                          {!!meta.error && meta.touched && (
                            <InputMessage variant="error">
                              {meta.error}
                            </InputMessage>
                          )}
                        </Stack>
                      )}
                    </Field>
                  ) : (
                    <></>
                  )}

                  {type === "Voucher" ? (
                    <Field name="expiryDates">
                      {({ field, meta }: FieldProps<string>) => (
                        <Stack spacing="xs">
                          <FormLabel htmlFor="expiryDates">
                            Expiry Date
                          </FormLabel>
                          <InputGroup inputSize={40}>
                            <InputText
                              type="date"
                              size={40}
                              errors={!!meta.error && meta.touched}
                              {...field}
                            />
                          </InputGroup>
                          {!!meta.error && meta.touched && (
                            <InputMessage variant="error">
                              {meta.error}
                            </InputMessage>
                          )}
                        </Stack>
                      )}
                    </Field>
                  ) : (
                    <></>
                  )}
                </FormWrapper>
              </DialogContent>
              <DialogFooter>
                <Stack
                  display="flex"
                  flexDirection="row"
                  spacing="xs"
                  flex={1}
                  style={{ gap: 15 }}
                >
                  <Button
                    block
                    variant="outline"
                    onClick={() => handleCloseModal()}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    isLoading={isFormLoading}
                    block
                    variant="primary"
                  >
                    Update {type} Stock
                  </Button>
                </Stack>
              </DialogFooter>
            </Form>
          );
        }}
      </Formik>
    </Dialog>
  );
};

export default CatalogStockUpdateModal;
